import { Clock } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useHistorico, type Dominio } from '@/modules/kanban';

interface Props {
  cardId: string;
  dominio?: Dominio;
}

const TIPO_LABEL: Record<string, string> = {
  coluna_movida: 'Mudança de fase',
  substatus_alterado: 'Sub-status alterado',
  responsavel_alterado: 'Responsável alterado',
  triagem_ambiental_gerada: 'Triagem ambiental vinculada',
  comentario: 'Comentário',
  criado: 'Processo criado',
  atualizado: 'Atualização',
  observacao: 'Observação',
};

const PIPE_ORIGEM_LABEL: Record<string, string> = {
  triagem_credito: 'Triagem',
  fno: 'Banco da Amazônia / FNO',
  bb: 'Banco do Brasil',
  sicoob_credisul: 'Sicoob Credisul',
  sicoob_fronteiras: 'Sicoob Fronteiras',
  rabobank: 'Rabobank',
  sicredi: 'Sicredi',
  bradesco: 'Bradesco',
  credisis: 'Credisis',
  cedulas_registro: 'Emissão da Cédula e Cartório',
  comprovacoes: 'Comprovações',
  triagem_ambiental: 'Triagem Ambiental',
  ambiental_desembargo: 'Desembargo Ambiental',
};

function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

function fmtDateOnly(iso: string | null | undefined): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('pt-BR');
  } catch {
    return iso;
  }
}

function descricaoEvento(e: Record<string, unknown>): string {
  const tipo = (e.tipo as string | null | undefined) ?? '';
  if (tipo === 'coluna_movida' && e.coluna_para_nome) {
    const de = e.coluna_de_nome as string | null | undefined;
    return de
      ? `${de} → ${e.coluna_para_nome}`
      : String(e.coluna_para_nome);
  }
  if (tipo === 'substatus_alterado') {
    return `Sub-status: ${(e.substatus_de as string | null | undefined) ?? '—'} → ${(e.substatus_para as string | null | undefined) ?? '—'}`;
  }
  return (e.descricao as string | null | undefined) ?? '';
}

function isAjusteAutomaticoSemMudanca(e: Record<string, unknown>): boolean {
  return e.tipo === 'coluna_movida'
    && String(e.coluna_de_nome || '').trim() !== ''
    && String(e.coluna_de_nome || '') === String(e.coluna_para_nome || '');
}

function isHistoricoTecnico(e: Record<string, unknown>): boolean {
  const desc = String(e.descricao || '');
  return isAjusteAutomaticoSemMudanca(e)
    || /card migrado/i.test(desc)
    || /n[aã]o gerar card filho/i.test(desc)
    || /limpeza da institui[cç][aã]o definitiva/i.test(desc)
    || /reposicionado para entrada/i.test(desc);
}

function isEventoDocumental(e: Record<string, unknown>): boolean {
  const desc = String(e.descricao || '');
  return /documentos?/i.test(desc)
    || /checklist/i.test(desc)
    || /or[çc]amentos?/i.test(desc)
    || /proposta comercial/i.test(desc)
    || /material t[eé]cnico/i.test(desc)
    || /migra[cç][aã]o segura/i.test(desc);
}

function isEventoHumano(e: Record<string, unknown>): boolean {
  return e.tipo === 'comentario';
}

function EventRow({ event }: { event: Record<string, unknown> }) {
  const tipo = (event.tipo as string | null | undefined) ?? '';
  const tipoLabel = TIPO_LABEL[tipo] ?? tipo ?? 'Evento';
  const desc = descricaoEvento(event);
  const dataIso = (event.created_at ?? event.criado_em) as string | null | undefined;
  const ator = (event.ator_nome as string | null | undefined) ?? null;
  const origemId = event.processo_origem_id as string | number | null | undefined;
  const origemTipo = String(event.processo_origem_tipo || '');
  const origemLabel = PIPE_ORIGEM_LABEL[origemTipo] || origemTipo.replaceAll('_', ' ');

  return (
    <li className="text-sm border-b border-border last:border-0 pb-2 last:pb-0">
      <div className="flex items-baseline justify-between gap-2 flex-wrap">
        <span className="font-medium text-text">{tipoLabel}</span>
        <span className="text-xs text-text-muted">{fmtDateTime(dataIso)}</span>
      </div>
      {desc && <p className="text-text-2 text-[0.82rem] mt-0.5">{desc}</p>}
      {origemId && (
        <p className="text-text-muted text-xs mt-0.5">
          Origem: Card #{String(origemId)}{origemLabel ? ` • ${origemLabel}` : ''}
        </p>
      )}
      {ator && <p className="text-text-muted text-xs mt-0.5">por {ator}</p>}
    </li>
  );
}

function EventGroup({ events }: { events: Record<string, unknown>[] }) {
  const grouped = new Map<string, Record<string, unknown>[]>();
  for (const event of events) {
    const dataIso = (event.created_at ?? event.criado_em) as string | null | undefined;
    const key = fmtDateOnly(dataIso) || 'Sem data';
    if (!grouped.has(key)) grouped.set(key, []);
    grouped.get(key)!.push(event);
  }
  return (
    <div className="space-y-3">
      {Array.from(grouped.entries()).map(([dateLabel, rows]) => (
        <div key={dateLabel}>
          <p className="text-[0.7rem] font-semibold uppercase tracking-wide text-text-muted mb-1.5">
            {dateLabel}
          </p>
          <ul className="space-y-2">
            {rows.map((event) => (
              <EventRow key={String(event.id)} event={event} />
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

export function HistoricoSection({ cardId, dominio = 'credito' }: Props) {
  const { data, isLoading, error } = useHistorico(dominio, cardId);
  const [showAll, setShowAll] = useState(false);
  const [showTechnical, setShowTechnical] = useState(false);

  const resumo = useMemo(() => {
    const rows = (data ?? []).map((e) => e as Record<string, unknown>);
    const autoCount = rows.filter(isAjusteAutomaticoSemMudanca).length;
    const technical = rows.filter((e) => (
      isHistoricoTecnico(e)
      && !isAjusteAutomaticoSemMudanca(e)
      && !isEventoDocumental(e)
      && !isEventoHumano(e)
    ));
    const relevantes = rows.filter((e) => (
      !isHistoricoTecnico(e)
      && !isEventoDocumental(e)
    ));
    return {
      autoCount,
      destaque: relevantes.slice(0, 3),
      restante: relevantes.slice(3),
      technical,
    };
  }, [data]);

  return (
    <section className="border border-border rounded-lg p-4 bg-surface-2 mb-3">
      <h3 className="text-[0.78rem] font-semibold uppercase tracking-wide text-text-muted mb-3 flex items-center gap-2">
        <span
          className="inline-flex items-center justify-center rounded-md shrink-0"
          style={{
            width: 24,
            height: 24,
            background: 'rgba(217,119,87,0.10)',
            color: '#d97757',
          }}
        >
          <Clock size={14} strokeWidth={1.75} />
        </span>
        <span>Histórico</span>
      </h3>

      {isLoading && <p className="text-sm text-text-muted">Carregando…</p>}
      {error && <p className="text-sm text-danger">Falha ao carregar histórico.</p>}
      {data && data.length === 0 && <p className="text-sm text-text-muted">Nenhum evento registrado.</p>}
      {data && data.length > 0 && (
        <div className="space-y-3">
          {resumo.autoCount > 0 && (
            <div className="rounded-md border border-border bg-elevated px-3 py-2 text-sm">
              <div className="font-medium text-text">Ajustes automáticos de fase</div>
              <p className="text-text-2 text-[0.82rem] mt-0.5">
                {resumo.autoCount} registros agrupados
              </p>
            </div>
          )}

          <EventGroup events={showAll ? resumo.destaque.concat(resumo.restante) : resumo.destaque} />

          {resumo.restante.length > 0 && (
            <button
              type="button"
              onClick={() => setShowAll((v) => !v)}
              className="text-xs font-medium text-accent hover:underline"
            >
              {showAll ? 'Ocultar histórico completo' : 'Ver histórico completo'}
            </button>
          )}

          {resumo.technical.length > 0 && (
            <div className="border border-border rounded-md bg-elevated">
              <button
                type="button"
                onClick={() => setShowTechnical((v) => !v)}
                className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left"
              >
                <span className="text-[0.78rem] font-semibold uppercase tracking-wide text-text-muted">
                  Histórico técnico
                </span>
                <span className="text-xs text-text-muted">
                  {showTechnical ? 'Ocultar' : 'Recolhido'}
                </span>
              </button>
              {showTechnical && (
                <div className="border-t border-border px-3 py-2">
                  <EventGroup events={resumo.technical} />
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
